// Birthdays library — computes who's celebrating today, tomorrow, or in the
// coming days, in Chile timezone. The data lives in /public/data/birthdays.json.
// Estructura por item:
//   { name, date: "YYYY-MM-DD", member_id?, is_pet?, type?, member_ids? }
//
// - `member_id`   → si el cumpleañero tiene login (papa/mama/tati/...)
// - `is_pet`      → true pa' Doby (banner con estilo perro 🐶)
// - `type: anniversary` + `member_ids: [...]` → aniversario compartido

let _cache = null;

export async function getBirthdays() {
  if (_cache) return _cache;
  const res = await fetch("/data/birthdays.json");
  if (!res.ok) return [];
  _cache = await res.json();
  return _cache;
}

// Devuelve la fecha "hoy" en zona horaria Chile (América/Santiago),
// independiente del reloj del navegador.
export function todayInChile() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Santiago",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now);
  const y = parseInt(parts.find((p) => p.type === "year").value, 10);
  const m = parseInt(parts.find((p) => p.type === "month").value, 10);
  const d = parseInt(parts.find((p) => p.type === "day").value, 10);
  return { year: y, month: m, day: d };
}

function parseIsoDate(iso) {
  const [y, m, d] = String(iso).split("-").map(Number);
  return { year: y, month: m, day: d };
}

// Días desde hoy hasta el próximo cumpleaños (0 = hoy).
function daysUntil(bd, today) {
  const t = new Date(today.year, today.month - 1, today.day);
  let target = new Date(today.year, bd.month - 1, bd.day);
  if (target < t) target = new Date(today.year + 1, bd.month - 1, bd.day);
  return Math.round((target - t) / (1000 * 60 * 60 * 24));
}

const MONTHS_ES = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

export function formatDay(bd) {
  return `${bd.day} de ${MONTHS_ES[bd.month - 1]}`;
}

// Contexto de cumpleaños pa' el usuario actual.
export async function computeBirthdayContext(currentMember) {
  const list = await getBirthdays();
  const today = todayInChile();
  const items = list.map((item) => {
    const bd = parseIsoDate(item.date);
    return {
      ...item,
      bd,
      days_until: daysUntil(bd, today),
      // Edad que cumple este año (o años del aniversario)
      turning_age: today.year - bd.year,
    };
  });

  // ¿Es MI cumpleaños hoy?
  const selfToday = items.find(
    (i) => i.days_until === 0 && i.member_id === currentMember
  );

  // ¿Es mi aniversario hoy? (papa/mama)
  const anniversaryToday = items.find(
    (i) => i.days_until === 0 && i.type === "anniversary"
      && Array.isArray(i.member_ids) && i.member_ids.includes(currentMember)
  );

  // Cumpleaños de OTROS hoy (no yo, no aniversario)
  const othersToday = items.filter(
    (i) => i.days_until === 0
      && i.member_id !== currentMember
      && i.type !== "anniversary"
  );

  // Próximos cumpleaños en los siguientes 3 días (no hoy)
  const upcoming = items
    .filter((i) => i.days_until >= 1 && i.days_until <= 3)
    .sort((a, b) => a.days_until - b.days_until);

  return { today, selfToday, anniversaryToday, othersToday, upcoming };
}

// LocalStorage key: audio de cumple reproducido hoy pa' este miembro.
// Evita que se repita en cada refresh dentro del mismo día.
export function bdayAudioAlreadyPlayed(member, today) {
  if (typeof localStorage === "undefined") return true;
  const key = `recetario_bday_played_${today.year}${String(today.month).padStart(2, "0")}${String(today.day).padStart(2, "0")}_${member}`;
  return localStorage.getItem(key) === "1";
}

export function markBdayAudioPlayed(member, today) {
  if (typeof localStorage === "undefined") return;
  const key = `recetario_bday_played_${today.year}${String(today.month).padStart(2, "0")}${String(today.day).padStart(2, "0")}_${member}`;
  localStorage.setItem(key, "1");
}
